import React, { createContext, useContext } from "react"
import { Redirect } from "react-router"
import { ProgramAndCoursesInfo } from "../../../../types/gqlTypes/graphql"
import { FeatureFlagContext } from "../../../mp-core/FeatureFlagProvider"
import { ProgramsContext } from "../../../mp-shared-components/ready-shared-components/components/Navs"
import { FeatureFlagName } from "../../../mp-utils/generic-utils/feature-flags/types"
import Loader from "../../../mp-utils/ready-utils/components/Loader"
import { Column } from "../../components/Table"
import {
  DEFAULT_DISPLAYED_PROPERTIES,
  FILTERS_BY_ROUTE,
  HEADER_COLUMNS,
  NAV_ITEMS,
  REQUEST_PARAMS
} from "../../constants"
import { CubeRequestParams, UserGoalsData, NavItem } from "../../types"
import {
  useAvailableGoals,
  useReportingTableData,
  useReportingExcludedWidgets,
  useNavItemsUnderFeatureFlagAndLicense,
  useProgramsWithOverallScoreRange,
  useReportingFiltersUnderFeatureFlag,
  useReportingProgramAndCourses
} from "./hooks"

type ReportingContextState = {
  // Reporting programsAndCourses
  reportingProgramsAndCourses: ProgramAndCoursesInfo[]
  reportingQuestionsProgramsAndCourses: ProgramAndCoursesInfo[]

  // Goals Data
  availableGoals: UserGoalsData[]
  availableGoalsTestsMapping: Record<string, string[]>
  availableGoalsProgramCourses: ProgramAndCoursesInfo[]
  availableGoalsLoading: boolean
  refetchGoalsData?: () => void

  // Overall Programs
  programsWithOverallScoreRange: string[]
  programsWithOverallScoreRangeLoading: boolean

  // Reporting Table Data
  reportingTableData: {
    headerColumns: Record<string, Record<string, Column[]>>
    displayedProperties: Record<string, Record<string, string[]>>
    requestParams: Record<string, Record<string, CubeRequestParams>>
  }

  // Reporting Excluded Widgets
  excludedWidgets: string[]

  // Reporting Filters By Route
  filtersByRoute: Record<string, string[]>

  // Reporting Nav items
  reportingNavItems: NavItem[]
  getRedirectPathForReportingRoute: (path: string) => string
}

export const ReportingContext = createContext<ReportingContextState>({
  // Reporting programsAndCourses
  reportingProgramsAndCourses: [],
  reportingQuestionsProgramsAndCourses: [],

  // Goals Data
  availableGoals: [],
  availableGoalsTestsMapping: {},
  availableGoalsProgramCourses: [],
  availableGoalsLoading: false,

  // Overall Programs
  programsWithOverallScoreRange: [],
  programsWithOverallScoreRangeLoading: false,

  // Reporting Table Data
  reportingTableData: {
    headerColumns: HEADER_COLUMNS,
    displayedProperties: DEFAULT_DISPLAYED_PROPERTIES,
    requestParams: REQUEST_PARAMS
  },

  // Reporting Excluded Widgets
  excludedWidgets: [],

  // Reporting Filters By Route
  filtersByRoute: FILTERS_BY_ROUTE,

  // Reporting Nav items
  reportingNavItems: [],
  getRedirectPathForReportingRoute: () => "/reporting"
})

interface Props {
  redirectPath: string
}

const ReportingProvider: React.FC<Props> = ({ redirectPath, children }) => {
  const { programsAndCoursesLoading } = useContext(ProgramsContext)
  const { featureFlags, featureFlagsLoading } = useContext(FeatureFlagContext)

  const { reportingProgramsAndCourses, reportingQuestionsProgramsAndCourses } =
    useReportingProgramAndCourses()

  const {
    programsWithOverallScoreRange,
    programsWithOverallScoreRangeLoading
  } = useProgramsWithOverallScoreRange(
    !featureFlags?.[FeatureFlagName.reporting_module],
    !!featureFlags?.[
      FeatureFlagName.reporting_graphql_programs_with_overall_score_range
    ]
  )

  const {
    availableGoals,
    availableGoalsTestsMapping,
    availableGoalsProgramCourses,
    availableGoalsLoading,
    refetchGoalsData
  } = useAvailableGoals(
    reportingProgramsAndCourses,
    !featureFlags?.[FeatureFlagName.reporting_module]
  )

  const {
    moduleNavItems: reportingNavItems,
    getRedirectPathForRoute: getRedirectPathForReportingRoute
  } = useNavItemsUnderFeatureFlagAndLicense(NAV_ITEMS[0])

  const reportingTableData = useReportingTableData()
  const excludedWidgets = useReportingExcludedWidgets()

  const filtersByRoute = useReportingFiltersUnderFeatureFlag()

  if (
    !!featureFlagsLoading ||
    !!programsAndCoursesLoading ||
    !!programsWithOverallScoreRangeLoading
  ) {
    return <Loader />
  }

  if (!featureFlags?.[FeatureFlagName.reporting_module]) {
    return <Redirect to={redirectPath} />
  }

  return (
    <ReportingContext.Provider
      value={{
        // Reporting programsAndCourses
        reportingProgramsAndCourses,
        reportingQuestionsProgramsAndCourses,

        // Goals Data
        availableGoals,
        availableGoalsTestsMapping,
        availableGoalsProgramCourses,
        availableGoalsLoading,
        refetchGoalsData,

        // Overall Programs
        programsWithOverallScoreRange,
        programsWithOverallScoreRangeLoading,

        // Reporting Table Data
        reportingTableData,

        // Reporting Excluded Widgets
        excludedWidgets,

        // Reporting Filters By Route
        filtersByRoute,

        // Reporting Nav items
        reportingNavItems,
        getRedirectPathForReportingRoute
      }}
    >
      {children}
    </ReportingContext.Provider>
  )
}

export default ReportingProvider
